import axios from 'axios';
import { db } from './firebase';
import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';

const ensureFirebase = () => {
  if (!db) throw new Error('Firebase is not configured. Check VITE_FIREBASE_* env vars.');
};

// --- Generic Collection Helpers ---

export async function getCollection(collectionName, constraints = []) {
  ensureFirebase();
  const colRef = collection(db, collectionName);
  const q = query(colRef, ...constraints);
  const snapshot = await getDocs(q);
  // Spread data first, then overwrite id with doc.id to ensure the real persistence ID is used
  return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
}

export async function getDocument(collectionName, id) {
  ensureFirebase();
  const docRef = doc(db, collectionName, String(id));
  const snapshot = await getDoc(docRef);
  // Spread data first, then overwrite id with snapshot.id
  return snapshot.exists() ? { ...snapshot.data(), id: snapshot.id } : null;
}

export async function addDocument(collectionName, data) {
  ensureFirebase();
  const colRef = collection(db, collectionName);
  // Add timestamps
  const dataWithTimestamps = {
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  const docRef = await addDoc(colRef, dataWithTimestamps);
  return { id: docRef.id, ...dataWithTimestamps };
}

export async function updateDocument(collectionName, id, data) {
  ensureFirebase();
  const docRef = doc(db, collectionName, String(id));
  const updateData = {
    ...data,
    updatedAt: new Date().toISOString()
  };
  await setDoc(docRef, updateData, { merge: true });
  return { id, ...updateData };
}

export async function deleteDocument(collectionName, id) {
  ensureFirebase();
  const docRef = doc(db, collectionName, String(id));
  await deleteDoc(docRef);
  return id;
}

// --- Specific Service Functions ---

// Settings
export async function getAppSettings() {
  return getDocument('settings', 'app');
}

export async function saveAppSettings(data) {
  return updateDocument('settings', 'app', data);
}

export async function getHomepageSettings() {
  return getDocument('settings', 'homepage');
}

export async function saveHomepageSettings(data) {
  return updateDocument('settings', 'homepage', data);
}

export async function getAboutSettings() {
  return getDocument('settings', 'about');
}

export async function saveAboutSettings(data) {
  return updateDocument('settings', 'about', data);
}

// Calculators
export async function getCalculatorConfig(slug) {
  const configs = await getCollection('calculatorConfigs', [
    where('slug', '==', slug),
    where('isActive', '==', true),
    limit(1)
  ]);
  return configs.length > 0 ? configs[0] : null;
}

export async function listCalculatorConfigs() {
  return getCollection('calculatorConfigs', [orderBy('updatedAt', 'desc')]);
}

export async function saveCalculatorConfig(id, data) {
  // If id is provided, update, else add (but 'save' usually implies ID is known for update, or new entry)
  // For consistency with previous code which used setDoc with merge:
  ensureFirebase();
  if (id) {
    return updateDocument('calculatorConfigs', String(id), data);
  } else {
    return addDocument('calculatorConfigs', data);
  }
}

// Services
export const getServices = () => getCollection('services');
export const saveService = (id, data) => id ? updateDocument('services', id, data) : addDocument('services', data);
export const deleteService = (id) => deleteDocument('services', id);

// Projects
export const getProjects = () => getCollection('projects', [orderBy('createdAt', 'desc')]);
export const saveProject = (id, data) => id ? updateDocument('projects', id, data) : addDocument('projects', data);
export const deleteProject = async (id) => {
  try {
    const project = await getDocument('projects', id);
    if (project) {
      const imagesToDelete = [];
      if (project.image && !project.image.includes('no_image')) imagesToDelete.push(project.image);
      if (project.gallery && Array.isArray(project.gallery)) imagesToDelete.push(...project.gallery);

      await Promise.all(imagesToDelete.map(url => {
        const publicId = getPublicIdFromUrl(url);
        return publicId ? deleteFile(publicId) : Promise.resolve();
      }));
    }
  } catch (e) {
    console.error('Error cleaning up project images:', e);
  }
  return deleteDocument('projects', id);
};

// Blogs
export const getBlogs = () => getCollection('blogs', [orderBy('createdAt', 'desc')]);
export const saveBlog = (id, data) => id ? updateDocument('blogs', id, data) : addDocument('blogs', data);
export const deleteBlog = async (id) => {
  try {
    const blog = await getDocument('blogs', id);
    if (blog && blog.featuredImage && !blog.featuredImage.includes('no_image')) {
      const publicId = getPublicIdFromUrl(blog.featuredImage);
      if (publicId) await deleteFile(publicId);
    }
  } catch (e) {
    console.error('Error cleaning up blog image:', e);
  }
  return deleteDocument('blogs', id);
};

// Testimonials
export const getTestimonials = () => getCollection('testimonials');
export const saveTestimonial = (id, data) => id ? updateDocument('testimonials', id, data) : addDocument('testimonials', data);
export const deleteTestimonial = (id) => deleteDocument('testimonials', id);

// Team
export const getTeamMembers = () => getCollection('team');
export const saveTeamMember = (id, data) => id ? updateDocument('team', id, data) : addDocument('team', data);
export const deleteTeamMember = (id) => deleteDocument('team', id);

// --- Storage / File Upload (Cloudinary) ---
// -- https://console.cloudinary.com/  images saved here---
const CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'dfmrjbprj';
const UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || 'ozfvbqjr';
const API_KEY = import.meta.env.VITE_CLOUDINARY_API_KEY || '592439451272852';
const API_SECRET = import.meta.env.VITE_CLOUDINARY_API_SECRET || 'AvyG1oPaUuil8BjMaqn-iY8PqBY';

async function generateSignature(params, apiSecret) {
  const data = Object.keys(params)
    .sort()
    .map(key => `${key}=${params[key]}`)
    .join('&') + apiSecret;

  const msgBuffer = new TextEncoder().encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-1', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

function getPublicIdFromUrl(url) {
  if (!url) return null;
  try {
    const parts = url.split('/upload/');
    if (parts.length < 2) return null;
    const pathParts = parts[1].split('/');
    if (pathParts[0].startsWith('v') && !isNaN(parseInt(pathParts[0].substring(1)))) {
      pathParts.shift();
    }
    const publicIdWithExt = pathParts.join('/');
    return publicIdWithExt.substring(0, publicIdWithExt.lastIndexOf('.'));
  } catch (e) {
    console.error('Error extracting public ID:', e);
    return null;
  }
}

export async function deleteFile(publicId) {
  if (!publicId) return;
  const timestamp = Math.round((new Date()).getTime() / 1000);
  const params = {
    public_id: publicId,
    timestamp: timestamp
  };

  const signature = await generateSignature(params, API_SECRET);

  const formData = new FormData();
  formData.append('public_id', publicId);
  formData.append('api_key', API_KEY);
  formData.append('timestamp', timestamp);
  formData.append('signature', signature);

  try {
    const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/destroy`, {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    if (data.result !== 'ok') {
      console.warn('Cloudinary delete warning:', data);
    }
  } catch (error) {
    console.error('Cloudinary delete failed:', error);
  }
}

export async function uploadFile(file, onProgress) {
  if (!file) throw new Error('No file provided');

  const MAX_SIZE = 10 * 1024 * 1024; // 10MB
  if (file.size > MAX_SIZE) {
    throw new Error(`File is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum allowed size is 10MB.`);
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);

  try {
    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
      formData,
      {
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        }
      }
    );

    const data = response.data;

    return {
      name: data.original_filename,
      url: data.secure_url,
      path: data.public_id,
      size: data.bytes,
      type: data.format
    };
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    throw error.response?.data?.error?.message || error.message || 'Upload failed';
  }
}
